/**
 * Pure helpers for BodyProfileForm, kept out of the component for the same
 * reason `goalReadout.ts` is: what needs checking is the branching, not the
 * markup, and a node:test can check a plain function without mounting React.
 */
import { ACTIVITY_LEVELS, type ActivityLevel } from '@loggy/shared';
import type { ZodError } from 'zod';

/**
 * A human description of what each level means in practice, not the bare
 * enum word — "moderate" alone means nothing, and picking the wrong one
 * quietly skews every energy estimate downstream.
 */
export const ACTIVITY_LEVEL_LABELS: Record<ActivityLevel, string> = {
  sedentary: 'Desk job, little to no exercise',
  light: 'Light exercise 1–3 days a week',
  moderate: 'Moderate exercise 3–5 days a week',
  high: 'Hard exercise 6–7 days a week',
  'very-high': 'Very hard exercise, or a physical job on top of training',
};

/** The options a <Select> renders, in the order `ACTIVITY_LEVELS` defines —
 *  least to most active — rather than re-sorting them. */
export const ACTIVITY_LEVEL_OPTIONS: { value: ActivityLevel; label: string }[] = ACTIVITY_LEVELS.map(
  (level) => ({ value: level, label: ACTIVITY_LEVEL_LABELS[level] }),
);

/** The three fields a person can mistype; `sex` and `activityLevel` come
 *  from a closed Select and can't produce a validation error worth showing. */
export type BodyProfileFieldErrors = Partial<
  Record<'sex' | 'birthDate' | 'heightCm' | 'activityLevel', string>
>;

/**
 * Turns a zod validation failure into a message next to the field that
 * caused it, rather than one generic banner — the brief's own requirement,
 * so a person editing height never has to guess it was the date that failed.
 */
export function fieldErrorsFromZodError(error: ZodError): BodyProfileFieldErrors {
  const result: BodyProfileFieldErrors = {};
  for (const issue of error.issues) {
    const key = issue.path[0];
    // Every field the schema can reject needs a message, or the form just
    // refuses to submit and says nothing. `sex` reaches here because it now
    // starts unchosen on purpose — the app will not guess which constant of
    // the equation to use.
    if (key === 'sex' && result.sex === undefined) {
      result.sex = 'Choose which form of the equation to use.';
    }
    if (key === 'activityLevel' && result.activityLevel === undefined) {
      result.activityLevel = 'Choose an activity level.';
    }
    if (key === 'birthDate' && result.birthDate === undefined) {
      result.birthDate = 'Enter a birth date.';
    }
    if (key === 'heightCm' && result.heightCm === undefined) {
      result.heightCm =
        issue.code === 'too_small' || issue.code === 'too_big'
          ? 'Enter a height between 80 and 260 cm.'
          : 'Enter a height in cm.';
    }
  }
  return result;
}
